/**
 * OverlayDiffBoard — single-board union view.
 *
 * Renders the union of before + after modules on one InfiniteBoard. Removed
 * modules come from `before`; added/modified/unchanged come from `after`.
 * Per-module diff status drives the CSS classes via DiffProvider.
 */
import { useMemo, useRef } from "react";
import type { ChangeSet } from "@archlang/engine";
import type { DependencyEdge, Module } from "@archlang/scene/arch-model";
import { DiffProvider } from "./DiffContext.js";
import { mergeModules } from "./mergeModules.js";
import { InfiniteBoard } from "../board/InfiniteBoard.js";
import { flattenModulesWithParent } from "@archlang/scene/arch-model";
import { createBoardEngine } from "@archlang/scene/board";
import { NodeColoringProvider, type NodeColoring } from "../board/coloring/index.js";
import { buildDiffColoring } from "../board/coloring/adapters.js";

export interface OverlayDiffBoardProps {
  readonly before: readonly Module[];
  readonly after: readonly Module[];
  readonly beforeEdges: readonly DependencyEdge[];
  readonly afterEdges: readonly DependencyEdge[];
  readonly changeSet: ChangeSet;
  /** Node colouring override (stroke + minimap). Default: diff colouring built
   *  from the merged union — pass one to share it with outer chrome (Studio's
   *  legend hover/pin builds it once upstream). */
  readonly coloring?: NodeColoring;
  /** Board node ids to highlight (legend hover/pin). */
  readonly memberHighlightIds?: readonly string[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string | null) => void;
}

export function OverlayDiffBoard({
  before,
  after,
  beforeEdges,
  afterEdges,
  changeSet,
  coloring,
  memberHighlightIds,
  selectedId,
  onSelect,
}: OverlayDiffBoardProps) {
  const merged = useMemo(() => mergeModules(before, after), [before, after]);
  const mergedEdges = useMemo(() => {
    // Union edges keyed by (from, to-interface) — after side wins on
    // overlap so unchanged edges aren't drawn twice.
    const byKey = new Map<string, DependencyEdge>();
    const key = (e: DependencyEdge) =>
      `${e.fromModulePath.join(".")}|${e.toInterfacePath.join(".")}`;
    for (const e of beforeEdges) byKey.set(key(e), e);
    for (const e of afterEdges) byKey.set(key(e), e);
    return [...byKey.values()];
  }, [beforeEdges, afterEdges]);
  const space = useMemo(
    () =>
      // One board-build path: the lazy engine at full depth == the old eager
      // `buildBoardFromModules` (engine.test.ts), now the only builder.
      createBoardEngine(flattenModulesWithParent(merged), mergedEdges).getSpace({
        path: [],
        depth: Infinity,
      }),
    [merged, mergedEdges],
  );
  const fallbackColoring = useMemo(
    () => (coloring ? null : buildDiffColoring(merged, changeSet)),
    [coloring, merged, changeSet],
  );
  const nodeColoring = coloring ?? fallbackColoring!;
  const ref = useRef<HTMLDivElement>(null);
  return (
    <DiffProvider changeSet={changeSet}>
      <NodeColoringProvider value={nodeColoring}>
        <div ref={ref} className="overlay-diff-board relative h-full">
          <InfiniteBoard
            root={space}
            selectedId={selectedId}
            memberHighlightIds={memberHighlightIds}
            onSelect={onSelect}
          />
        </div>
      </NodeColoringProvider>
    </DiffProvider>
  );
}
